import { Grid } from '@material-ui/core'
import ActionsToggleButton from 'components/ActionsToggleButton'
import Button from 'components/Buttons'
import ContentWrapper from 'components/ContentWrapper'
import RasterList from 'components/lists/rasterList'
import TopNavigation from 'components/navigation/TopNavigation'
import RasterEditDrawer from 'containers/drawers/RasterDrawer'
import { RasterModel } from 'data/models/RasterModel'
import { useRasterServices } from 'data/services'
import { createNotification } from 'factories/notificationFactory'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Dictionary } from 'types'
import { IRootReduxState } from 'types/reducers'
import { useIntl } from 'react-intl'
import ConfirmationDialog from 'containers/dialogs/ConfirmationDialog'
import TablePagination from '@material-ui/core/TablePagination'
import ConfirmDialog from 'containers/dialogs/ConfirmDialog'


const RasterPage: React.FC = () => {
  const [rasters, setRasters] = React.useState<RasterModel[]>([])
  const [selectedRaster, setSelectedRaster] = React.useState<RasterModel | undefined>(undefined)
  const [deleteRaster, setDeleteRaster] = React.useState<number | undefined>(undefined);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10); // or whatever default you like
  const [pageOfRasters, setPageOfRasters] = React.useState<RasterModel[]>([]);
  const [filterText, setFilterText] = React.useState('');

  const intl = useIntl()
  const currentUser = useSelector((state: IRootReduxState) => state.app.user.current)
  const rasterService = useRasterServices()
  const [changesExist, setChangesExist] = React.useState(false)
  const [confirmDiscardChanges, setConfirmDiscardChanges] = React.useState(false)
  const discardAndClose = (pendingChangesExist: boolean) => {
    if (pendingChangesExist) {
      setConfirmDiscardChanges(true)
    } else {
      setSelectedRaster(undefined)
      setChangesExist(false)
    }
  }

  React.useEffect(() =>{
    rasterService.getAllRaster().then((result) => {
      const sortedResult = result.sort((a,b) => b.Id - a.Id)
      setRasters(sortedResult)
    })
  },[])


  React.useEffect(() => {
    const startIndex = page * rowsPerPage
    setPageOfRasters(rasters.slice(startIndex, startIndex + rowsPerPage))
  }, [rasters, page, rowsPerPage])


const refreshRasters = (closeDrawer = true) => {
  rasterService.getAllRaster().then((result) => {
    const sorted = [...result].sort((a, b) => b.Id - a.Id)
    closeDrawer && setSelectedRaster(undefined)
    setRasters(sorted)
    setPage(0)
  })
}

const checkRasterNameExists = (name: string | undefined, currentId?: number): boolean => {
    if (!name || !rasters) return false;
    const lowerCaseName = name.toLowerCase().trim();
        return rasters.some(r => 
      r.Name?.toLowerCase().trim() === lowerCaseName && r.Id !== currentId
    );
  };

  const createNew = () => {
    const raster = new RasterModel()
    raster.CompanyId = currentUser?.CompanyId
    setSelectedRaster(raster)
  }

  const handleCreate = (item: RasterModel) => {
    if (checkRasterNameExists(item.Name)) {
      createNotification.info('A raster with this name already exists');
      return;
    }
    rasterService.createRaster(item).then(() => {
      createNotification.success('Raster Created Successfully')
      refreshRasters()
    })
  }

  const handleUpdate = (id: number, data: Dictionary<any>) => {
    if (data.Name && checkRasterNameExists(data.Name, id)) {
      createNotification.info('A raster with this name already exists');
      return;
    }
    rasterService.updateRaster(id, data as RasterModel).then(() => {
      createNotification.success(
        intl.formatMessage({
          id: 'update-success-raster',
          defaultMessage: 'Raster Updated Successfully',
        })
      )
      refreshRasters()
    })
  }

  const handleDelete = (id: number) => {
    rasterService.DeleteRaster(id).then(() => {
      createNotification.success(
        intl.formatMessage({
          id: 'delete-success-raster',
          defaultMessage: 'Raster Deleted Successfully',
        })
      )
      setDeleteRaster(undefined);
      refreshRasters()
    })
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Settings / Rasters</title>
      </Helmet>

      <TopNavigation
        componentsLeft={
          <Grid container direction="row" alignItems="center">
            <Grid item>
              <ActionsToggleButton>
                <Button.CreateButton label={intl.formatMessage({ id: 'create.raster', defaultMessage: 'New Raster Collection' })} click={() => createNew()} />
              </ActionsToggleButton>
            </Grid>
          </Grid>
        }
      />
      
      <ConfirmationDialog
        message={
          <>
            {intl.formatMessage({ id: 'delete.raster.message', defaultMessage: 'Please confirm that you would like to delete this raster?' })}
          </>
        }
        title={intl.formatMessage({ id: 'delete.raster.title', defaultMessage: 'Delete Raster' })}
        open={deleteRaster !== undefined}
        handleCancel={() => setDeleteRaster(undefined)}
        handleOk={() => deleteRaster && handleDelete(deleteRaster)}
      />

      <ContentWrapper>
        <RasterList raster={pageOfRasters} handleSelect={(item) => setSelectedRaster(item)} />
        <TablePagination
        component="div"
        count={rasters.length}
        page={page}
        onChangePage={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10))
          setPage(0)
        }}
      />


      </ContentWrapper>
      <ConfirmDialog
        open={confirmDiscardChanges}
        id="raster.confirm.discard"
        onConfirm={() => {
          setConfirmDiscardChanges(false)
          setSelectedRaster(undefined)
          setChangesExist(false)
        }}
        onCancel={() => setConfirmDiscardChanges(false)}
      />
      <RasterEditDrawer setSelectedRaster={setSelectedRaster} selectedRaster={selectedRaster} handleCreate={handleCreate} handleUpdate={handleUpdate}
       handleDelete={id => setDeleteRaster(id)} handleCancel={(pendingChangesExist?: boolean) => discardAndClose(pendingChangesExist ?? false)} handleModifiedState={(isModified: boolean) => setChangesExist(isModified)} changesExist={changesExist} />
    </React.Fragment>
  )
}

export default RasterPage
